import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Trash2, Sparkles, Power, PowerOff, MessageSquare, ScrollText } from "lucide-react";
import api from "@/lib/api";

/**
 * /settings/ai-instructions — Admin chat to teach the AI persistent rules.
 *
 * Every message is distilled by the AI into a concise imperative rule that is
 * appended to the ``SYSTEM_PROMPT`` on every future LLM call. Admins can
 * inspect all saved rules, toggle them on/off, or delete them.
 */
export default function AiInstructionsSettings() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversation, setConversation] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ai-instructions");
      setRules(r.data?.rules || []);
    } catch (e) {
      toast.error("Erro ao carregar regras");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Auto-scroll conversation to bottom on new message
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [conversation, sending]);

  const send = async () => {
    const text = message.trim();
    if (!text) return;
    setSending(true);
    setConversation((c) => [...c, { role: "user", text, at: Date.now() }]);
    setMessage("");
    try {
      const r = await api.post("/ai-instructions/chat", { message: text });
      const reply = r.data?.reply || "Instrução guardada.";
      const savedRule = r.data?.rule;
      setConversation((c) => [...c, { role: "assistant", text: reply, rule: savedRule, at: Date.now() }]);
      if (savedRule) setRules((rs) => [...rs, savedRule]);
      toast.success("Regra guardada — passa a ser aplicada em todas as gerações futuras");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar instrução");
      setConversation((c) => [...c, { role: "assistant", text: "Não consegui interpretar. Reformule por favor.", error: true, at: Date.now() }]);
    } finally { setSending(false); }
  };

  const toggle = async (id, active) => {
    try {
      await api.patch(`/ai-instructions/${id}`, { active });
      setRules((rs) => rs.map((r) => (r.id === id ? { ...r, active } : r)));
    } catch { toast.error("Erro ao alternar regra"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover esta regra? A IA deixará de a considerar.")) return;
    try {
      await api.delete(`/ai-instructions/${id}`);
      setRules((rs) => rs.filter((r) => r.id !== id));
      toast.success("Regra removida");
    } catch { toast.error("Erro ao remover"); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
  };

  return (
    <div className="min-h-screen bg-muted/40 p-6 lg:p-8" data-testid="ai-instructions-root">
      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <div className="text-[10px] uppercase tracking-[0.25em] text-foreground font-semibold">Configuração global</div>
          <h1 className="font-display text-3xl text-foreground mt-1">Instruções à IA</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
            Diga à IA como deve escrever de aqui em diante. As instruções aqui gravadas
            são aplicadas a <b>todas</b> as gerações futuras — propostas, estimativas,
            emails ao cliente, "Melhorar com IA". Serão memorizadas até as remover.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* --- Chat panel --- */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col" data-testid="ai-chat-panel">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-foreground" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground">Dar instrução</div>
            </div>
            <div ref={scrollRef}
                  className="flex-1 overflow-y-auto px-5 py-4 space-y-3 max-h-[520px] min-h-[360px] bg-muted/40"
                  data-testid="ai-chat-scroll">
              {conversation.length === 0 && (
                <div className="text-xs text-muted-foreground italic text-center py-14">
                  Escreva uma instrução em linguagem natural.<br/>
                  Ex: "Nunca falar de IVA à taxa de 6% — os serviços da Caring são isentos de IVA."
                </div>
              )}
              {conversation.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                      data-testid={`msg-${m.role}-${i}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${
                    m.role === "user"
                      ? "bg-foreground text-background"
                      : m.error
                        ? "bg-[hsl(var(--danger-bg))] text-[#991B1B] border border-[hsl(var(--danger))]/30"
                        : "bg-card border border-border text-foreground"
                  }`}>
                    <div className="leading-relaxed whitespace-pre-wrap">{m.text}</div>
                    {m.rule && (
                      <div className="mt-2 pt-2 border-t border-border text-[11px] text-muted-foreground">
                        <div className="inline-flex items-center gap-1 text-foreground font-semibold uppercase tracking-wider text-[9px] mb-1">
                          <Sparkles className="w-3 h-3" /> Regra guardada
                        </div>
                        <div className="text-[12px] text-foreground">"{m.rule.text}"</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-card border border-border rounded-2xl px-4 py-2.5 text-xs text-muted-foreground inline-flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> A destilar instrução…
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-border p-3 bg-card">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ex: Nunca menciones IVA à taxa reduzida — os serviços da Caring são isentos de IVA."
                rows={3}
                className="w-full bg-card border border-border rounded-md px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40 resize-none"
                data-testid="ai-chat-input"
              />
              <div className="flex items-center justify-between mt-2">
                <div className="text-[10px] text-muted-foreground">Cmd/Ctrl + Enter para enviar</div>
                <button onClick={send} disabled={sending || !message.trim()}
                         className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-[#1E293B] text-background font-semibold rounded-full px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
                         data-testid="ai-chat-send">
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Guardar
                </button>
              </div>
            </div>
          </div>

          {/* --- Saved rules panel --- */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden flex flex-col" data-testid="rules-panel">
            <div className="px-5 py-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ScrollText className="w-4 h-4 text-foreground" />
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-foreground">Regras memorizadas</div>
              </div>
              <div className="text-xs text-muted-foreground" data-testid="rules-count">
                {rules.filter((r) => r.active).length} activas / {rules.length}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2 max-h-[620px] min-h-[440px]">
              {loading && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin"/> A carregar…</div>}
              {!loading && rules.length === 0 && (
                <div className="text-xs text-muted-foreground italic text-center py-14">
                  Ainda não guardou nenhuma regra.<br/>
                  Use o chat à esquerda para começar.
                </div>
              )}
              {rules.map((r) => (
                <div key={r.id}
                      className={`rounded-lg border p-3 flex items-start gap-3 ${
                        r.active
                          ? "bg-muted/40 border-border"
                          : "bg-muted border-border opacity-60"
                      }`}
                      data-testid={`rule-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground leading-relaxed">{r.text}</div>
                    <div className="text-[10px] text-muted-foreground mt-1.5">
                      {r.created_by_name || "—"} · {new Date(r.created_at).toLocaleString("pt-PT")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggle(r.id, !r.active)}
                             className={`p-1.5 rounded-md ${r.active ? "text-muted-foreground hover:bg-card" : "text-[hsl(var(--warning))] hover:bg-card"}`}
                             title={r.active ? "Desactivar (não aplica à IA)" : "Reactivar"}
                             data-testid={`rule-toggle-${r.id}`}>
                      {r.active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => remove(r.id)}
                             className="p-1.5 rounded-md text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
                             title="Remover regra"
                             data-testid={`rule-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
